<template id="select">
    <div
        :id="wrapperId"
        v-if="showSelect"
        :class="classList"
        @click="toggleDropdown($event)"
        @keydown="search"
        @keydown.up.down.space.enter.tab="handleKeydown($event)"
        @keyup.esc="hideDropdown()"
        @mouseover="setCurrentOptionFocusFromMouseOver($event)"
        role="listbox"
        ref="select-box-wrapper"
        :tabindex="selectConfig.tabIndex"
        :aria-label="selectConfig.label"
        :aria-controls="wrapperId + '-selected'"
        :aria-expanded="focused"
    >
        <div class="caret"></div>

        <select
            v-model="selected"
            :id="selectElementId"
            :class="[{ selected: hasValue }, selectConfig.styleClass]"
            @focus="focused = true"
            @blur="focused = false"
            :tabindex="selectConfig.tabIndex"
            :data-recurly="selectConfig.dataRecurly"
            @change.self.stop="makeNativeSelection($event.target.value)"
            :aria-label="selectConfig.label"
            :aria-required="this.ariaRequired"
            ref="select"
        >
            <option
                v-for="option in selectConfig.options"
                :value="option.value"
                :default="option.default"
                :aria-label="option.text"
            >
                {{ option.text }}
            </option>
        </select>

        <div class="select-selected" :id="wrapperId + '-selected'" role="region" aria-live="polite">
            {{ displayed }}
        </div>

        <label
            :aria-label="selectConfig.label"
            :for="selectElementId"
            :class="{ floated: initialized }"
        >
            {{ selectConfig.label }}
        </label>

        <ul class="select-items select-hide" ref="options-wrapper">
            <li
                class="select-option"
                v-for="option in selectConfig.options"
                v-if="!option.hidden"
                :value="option.value"
                @mouseover="mOver($event)"
                @click.self.stop="makeSelection(option.value)"
                :aria-label="option.text"
                ref="select-option"
            >
                {{ option.text }}
            </li>
        </ul>
    </div>
</template>

<script>
    export default {
        name: 'CustomSelect',
        props: {
            selectConfig: {
                type: Object
            },
            errorMessage: {
                type: String,
                required: false
            },
            hideIfOptionsLessThan: {
                type: Number,
                required: false
            },
        },
        data: function () {
            return {
                keysSoFar: '',
                resetKeysSoFarTimer: null,
                selected: '',
                userMadeSelection: false,
                preserveSelection: false,
                previousSelected: '',
                considered: 0,
                selectElementId: this.selectConfig.name,
                wrapperId: 'custom-selector-' + this.selectConfig.name,
                tabIndex: this.selectConfig.tabIndex,
                initialized: false,
                options: [],
                focused: false,
                opened: false,
                firstOption: true,
                value: '',
                ariaRequired: true,
                arrowCounter: 0
            };
        },
        computed: {
            hasValue: function () {
                return this.selected && this.selected !== '';
            },

            classList: function () {
                return [
                    'select-box-wrapper',
                    'field-wrapper',
                    'field-wrapper--floating-label',
                    'field-wrapper--' + this.selectConfig.id,
                    this.focused ? 'focused' : '',
                    this.errorMessage ? 'error' : ''
                ];
            },

            displayed() {
                for (let option of this.selectConfig.options) {
                    if (option.value === this.selected || option.text === this.selected) {
                        return option.text;
                    }
                }

                return null;
            },

            showSelect() {
                return (
                    this.hideIfOptionsLessThan === undefined ||
                    this.selectConfig.options.length >= this.hideIfOptionsLessThan
                );
            },

            expanded: function () {
                return this.opened;
            }
        },
        watch: {
            arrowCounter: function () {
                this.$nextTick(function () {
                    const selOption = this.$refs['select-option'][this.arrowCounter].clientHeight;
                    if (selOption) {
                        this.$refs['options-wrapper'].scrollTop = selOption * this.arrowCounter;
                    }
                });
            }
        },
        methods: {
            search(e) {
                // Simple sanity check, we should never get here if we're not focused, but it never hurts to check
                if (e.key.length > 1) return;
                if (!this.focused || !this.opened) this.showDropdown();
                if (this.resetKeysSoFarTimer !== null) {
                    clearTimeout(this.resetKeysSoFarTimer);
                    this.resetKeysSoFarTimer = null;
                }
                this.resetKeysSoFarTimer = setTimeout(() => {
                    this.keysSoFar = '';
                    this.resetKeysSoFarTimer = null;
                }, 500);
                this.keysSoFar += e.key;
                const options = this.selectConfig.options;
                this.resetOptionsFocus();

                for (let option in options) {
                    if (options[option].text.toLowerCase().startsWith(this.keysSoFar)) {
                        this.considered = option;
                        this.setCurrentOptionFocus();
                        this.arrowCounter = parseInt(option);
                        break;
                    }
                }
            },
            onArrowUp: function () {
                if (this.arrowCounter > 0) {
                    this.arrowCounter = this.arrowCounter - 1;
                }
            },
            onArrowDown: function () {
                this.arrowCounter = this.arrowCounter + 1;
            },
            /**
             * Return dropdown options array
             */
            getOptions: function () {
                return this.$refs['select-option'] || [];
            },

            /**
             * Reset all options to not have the class considered (which makes an option have a visual selection focus)
             */
            resetOptionsFocus: function () {
                let options = this.getOptions();
                for (let option of options) {
                    option.classList.remove('considered');
                }
            },

            /**
             * Check if the dropdown is opened
             */
            dropdownIsOpen: function () {
                return this.opened;
            },

            /**
             * Check if the dropdown has focus
             */
            dropdownIsFocused: function () {
                return this.focused;
            },

            /**
             * Show the dropdown
             */
            showDropdown: function () {
                this.focused = true;
                this.opened = true;

                if (this.selected !== '') {
                    this.preserveSelection = true;
                    this.previousSelected = this.selected;
                }
                this.$emit('focus');
            },

            /**
             * Hide the dropdown invoking reset options focus pointer
             */
            hideDropdown: function () {
                // reset options list pointer
                this.resetOptionsFocus();

                // reset the selected value to it's original state if no selection has been made
                if (!this.userMadeSelection) {
                    this.initialized = false;
                    this.selected = '';
                } else if (this.preserveSelection) {
                    this.selected = this.previousSelected;
                }

                // reset dropdown
                this.firstOption = true;
                this.considered = 0;
                this.focused = false;
                this.opened = false;
                this.preserveSelection = false;

                this.arrowCounter = 0;
                // remove focus from dropdown
                this.$emit('blur');
            },

            /**
             * Show/hide dropdown
             *
             * @param ev
             */
            toggleDropdown: function (ev) {
                // close any other open select input menus on the page
                if (!this.dropdownIsFocused()) {
                    let wrapper = ev.target.parentNode;
                    const selectWrapper = this.$refs['select-box-wrapper'];
                    if (wrapper !== selectWrapper) {
                        selectWrapper.classList.remove('focused');
                    }
                }

                if (this.dropdownIsOpen()) {
                    this.hideDropdown();
                } else {
                    this.showDropdown();
                }
            },

            /**
             * Set focus to the next element (after select an option from the options list)
             *
             * @param current
             */
            nextField: function (current) {
                if (!current || current.form === undefined) {
                    return;
                }
                for (let i = 0; i < current.form.elements.length; i++) {
                    if (current.form.elements[i].tabIndex === current.tabIndex + 1) {
                        current.form.elements[i].focus();
                        this.$emit('blur');

                        if (current.form.elements[i].type === 'text') {
                            current.form.elements[i].select();
                        }
                    }
                }
            },

            /**
             * Set focus on dropdown next option
             */
            nextOption: function () {
                let options = this.getOptions();
                if (this.firstOption) {
                    this.firstOption = false;
                } else if (!this.firstOption && this.considered < options.length - 1) {
                    this.considered++;
                    this.onArrowDown();
                }

                // set current focus
                this.setCurrentOptionFocus();
            },

            /**
             * Set focus on dropdown previous option
             */
            previousOption: function () {
                if (this.considered > 0) {
                    this.considered--;
                    this.onArrowUp();
                }

                // set current focus
                this.setCurrentOptionFocus();
            },

            /**
             * Controls tabbing and enter while using keyboard navigation
             *
             * @param ev
             */
            handleKeydown: function (ev) {
                // change options or select an option when the dropdown is opened
                if (ev.altKey) return;
                if (this.dropdownIsFocused() && this.dropdownIsOpen() && ev.code !== 'Tab') {
                    // reset options list pointer
                    this.resetOptionsFocus();

                    // tab through element options using tab & shift key
                    if (ev.code === 'ArrowUp') {
                        this.previousOption();
                    }
                    // tab through element options using tab
                    else if (ev.code === 'ArrowDown') {
                        this.nextOption();
                    }
                    // select an option if pressed enter/space
                    else if (ev.code === 'Enter' || ev.code === 'Space') {
                        let options = this.getOptions();

                        // select the current focused option
                        if (options[this.considered] !== undefined) {
                            this.makeSelection(options[this.considered].getAttribute('value'));
                        }
                        this.$emit('blur');
                        this.hideDropdown(ev);
                    }

                    // prevent showing native select options when the dropdown is closed
                    ev.preventDefault();
                    return false;
                }

                // show dropdown if the user hit arrow down, enter or space and prevent show native select options
                if (ev.code === 'ArrowDown' || ev.code === 'Enter' || ev.code === 'Space') {
                    if (!this.dropdownIsOpen()) {
                        this.showDropdown(ev, true);
                    }

                    // prevent showing native select options when the dropdown is closed
                    ev.preventDefault();
                    return false;
                }

                // closes the dropdown on tab (also includes shift+tab)
                if (ev.code === 'Tab') {
                    this.hideDropdown();
                }
            },

            /**
             * Set focus pointer to the current focused option
             *
             * @param ev
             */
            setCurrentOptionFocusFromMouseOver: function (ev) {
                if (this.dropdownIsOpen() && this.currentOnOptionsWrapper(ev)) {
                    this.resetOptionsFocus();

                    // show current focused option on dropdown root
                    let val = ev.target.innerText;
                    if (val) {
                        this.initialized = true;
                        this.selected = val;
                    }
                }
            },

            /**
             * Set the visual indication for the current focused option
             */
            setCurrentOptionFocus: function () {
                if (this.considered >= 0) {
                    const options = this.getOptions();
                    if (options[this.considered]) {
                        let consideredSelect = options[this.considered];

                        // add the class considered on the current focused option
                        consideredSelect.classList.add('considered');

                        // show current focused option on dropdown root
                        let val = consideredSelect.getAttribute('value');
                        if (val) {
                            this.initialized = true;
                            this.selected = val;
                        }
                    }
                }
            },

            //if the user starts using the mouse instead of the arrow keys, suppress arrow key functionality
            mOver: function (ev) {
                let selectorMain = document.getElementById(this.wrapperId);
                let optionsEls = selectorMain.getElementsByClassName('select-items');
                for (let oEl of optionsEls) {
                    oEl.classList.remove('considered');
                }
            },

            /**
             * Select current option
             *
             * @param val
             * @param isNativeSelection
             */
            makeSelection: function (val, isNativeSelection) {
                if (val !== null) {
                    this.selected = val;
                    this.userMadeSelection = true;
                }

                this.focused = false;
                this.opened = false;
                this.initialized = true;
                this.preserveSelection = false;
                this.arrowCounter = 0;
                this.$emit('input', {
                    value: this.selected
                });

                if (!isNativeSelection) {
                    this.nextField(this.$el);
                }
            },

            /**
             * Select current option while using native select element
             *
             * @param val
             */
            makeNativeSelection: function (val) {
                this.makeSelection(val, true);
            },

            /**
             * Close dropdown listener when user click outside dropdown
             *
             * @param ev
             */
            outsideClickListener: function (ev) {
                let currentOnSelectWrapper = this.currentOnSelectWrapper(ev);
                let currentOnOptionsWrapper = this.currentOnOptionsWrapper(ev);

                // hide the dropdown if the click was not in the dropdown area and it is opened
                if (!currentOnSelectWrapper && !currentOnOptionsWrapper && this.dropdownIsOpen()) {
                    this.hideDropdown();
                }
            },

            /**
             * Check if the event target has the select wrapper
             *
             * @param ev
             */
            currentOnSelectWrapper: function (ev) {
                let selectWrapper = this.$el;
                return selectWrapper.contains(ev.target);
            },

            /**
             * Check if the event target has the options wrapper
             *
             * @param ev
             */
            currentOnOptionsWrapper: function (ev) {
                let optionsWrapper = this.$refs['options-wrapper'];
                return optionsWrapper.contains(ev.target);
            }
        },

        /* Lifecycle hooks */

        created() {
            this.$emit('input', {
                value: this.selectConfig.value
            });
        },

        mounted() {
            // needed as vue 2 doesn't have a native click-outside listener
            document.addEventListener('mousedown', this.outsideClickListener);
        },

        destroyed() {
            // needed as vue 2 doesn't have a native click-outside listener
            document.removeEventListener('mousedown', this.outsideClickListener);
        }
    };
</script>
